import { useCallback, useMemo } from 'react'
import { Ban, Check, SlidersHorizontal } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchFraudRules, toggleFraudRule, SEC_STR as S, type FraudRuleRow } from './securityMoreApi'

/**
 * A18.11 — Правила анти-фрода. LIVE `GET /admin/antifraud/rules`; enable/disable
 * via `POST .../rules/:id/toggle`. Security-module lens over the A25 ruleset.
 */
export function FraudRulesScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<FraudRuleRow>[]>(
    () => [
      { key: 'name', header: S.ruleName, type: 'text' },
      { key: 'condition', header: S.condition, type: 'text', minWidth: 200 },
      { key: 'action', header: S.action, type: 'text', width: 110 },
      { key: 'enabled', header: S.enabledLbl, type: 'bool', width: 90, align: 'center' },
      { key: 'hits', header: S.hits, type: 'number', width: 120 },
    ],
    [],
  )

  const toggle = useCallback(
    async (row: FraudRuleRow) => {
      try {
        await toggleFraudRule(row.id, !row.enabled)
        toast(S.toggled, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<FraudRuleRow>[]>(
    () => [
      {
        id: 'enable',
        label: S.enable,
        icon: <Check size={14} />,
        visible: (r) => !r.enabled,
        onSelect: (r) => toggle(r),
      },
      {
        id: 'disable',
        label: S.disable,
        icon: <Ban size={14} />,
        danger: true,
        visible: (r) => r.enabled,
        onSelect: (r) => toggle(r),
      },
    ],
    [toggle],
  )

  const fetcher = useCallback(fetchFraudRules, [])
  return (
    <>
      <PageHeader title={S.fraudRules} screenId="A18.11" />
      <DataTable<FraudRuleRow>
        screenId="A18.11"
        permission={PERMISSIONS.AUDIT_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        rowActions={rowActions}
        emptyState={{ icon: <SlidersHorizontal size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
